import { Music } from "lucide-react";
import { cn } from "@/lib/utils";
import { danceStyleColor } from "@/lib/constants";
import { useDanceStyles } from "@/hooks/useDanceStyles";
import { formatTime, formatTimeCompact, type CalendarDay } from "@/lib/dateUtils";
import { getLessonSchedule } from "@/lib/lessonSchedule";
import type { Event } from "./EventCard";

interface CalendarDayCellProps {
  day: CalendarDay;
  events: Event[];
  onClick: () => void;
}

/**
 * How many events a month cell names before it gives up and counts.
 *
 * Three is what fits without the row growing taller than a thumb; past that
 * the cell says how many more there are and the day's modal lists them all.
 */
const MAX_CHIPS = 3;

export function CalendarDayCell({ day, events, onClick }: CalendarDayCellProps) {
  const hasEvents = events.length > 0;
  const shown = events.slice(0, MAX_CHIPS);
  const remaining = events.length - shown.length;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasEvents}
      aria-label={`${day.date.toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      })} — ${events.length === 1 ? "1 event" : `${events.length} events`}`}
      className={cn(
        "group relative flex flex-col items-stretch gap-0.5 p-1 text-left",
        "min-h-[84px] sm:min-h-[112px] transition-colors duration-200",
        "outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/50",
        day.isCurrentMonth ? "bg-card" : "bg-muted/20",
        hasEvents ? "cursor-pointer hover:bg-muted/40" : "cursor-default"
      )}
    >
      {/* Day number */}
      <span className="flex justify-center">
        <span
          className={cn(
            "flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium tabular-nums",
            day.isToday && "bg-primary text-primary-foreground",
            !day.isToday && (day.isCurrentMonth ? "text-foreground" : "text-muted-foreground/50")
          )}
        >
          {day.date.getDate()}
        </span>
      </span>

      {/* Event chips */}
      <span className="flex flex-col gap-px overflow-hidden">
        {shown.map((event) => (
          <MonthEventChip key={event._id} event={event} dimmed={!day.isCurrentMonth} />
        ))}
        {remaining > 0 && (
          <span className="px-1 text-[10px] font-medium text-muted-foreground">
            +{remaining} more
          </span>
        )}
      </span>
    </button>
  );
}

/**
 * One event inside a month cell.
 *
 * A month column is narrow, so the title gets the room and the time only
 * appears once the cell is wide enough to hold both without either being cut
 * to nothing.
 *
 * Where a social opens with a class, the time shown is when the dancing
 * starts rather than when the doors open — that is the number someone fitting
 * two dances into a night is looking for. The cap marks it, so the glyph
 * always means the same thing: this time is the floor, not the door.
 */
function MonthEventChip({ event, dimmed }: { event: Event; dimmed: boolean }) {
  const { styleMap, orderStyles } = useDanceStyles();
  const color = danceStyleColor(orderStyles(event.danceStyles, event.mainStyles), styleMap);
  const schedule = getLessonSchedule(event);
  const showsDanceStart = schedule.kind === "timed";
  const time = showsDanceStart ? schedule.danceStart : event.startTime;

  return (
    <span
      title={
        showsDanceStart
          ? `${event.title} — lesson from ${formatTime(event.startTime)}, dancing from ${formatTime(time)}`
          : `${formatTime(time)} · ${event.title}`
      }
      className={cn(
        // A phone's month cell is about 48px wide, so the horizontal padding is
        // shaved to the minimum there — every pixel is another letter of title.
        "flex items-baseline gap-1 rounded-sm border-l-2 pl-0.5 pr-0 sm:px-1 py-px",
        "text-[10px] leading-[14px] sm:text-[11px] sm:leading-4",
        dimmed && "opacity-50"
      )}
      style={{ borderLeftColor: color, backgroundColor: `${color}24` }}
    >
      <span className="hidden sm:flex flex-shrink-0 items-center gap-px text-muted-foreground tabular-nums">
        {showsDanceStart && <Music className="w-2.5 h-2.5" aria-hidden="true" />}
        {formatTimeCompact(time)}
      </span>
      <span className="truncate font-medium text-foreground">{event.title}</span>
    </span>
  );
}
